import type { Filter, SavedView, Sort } from "@estelle/domain";
import { useState } from "react";

import { canonicalJson } from "@/components/views/canonical";
import { useSavedViews } from "@/components/views/useSavedViews";
import { can, usePermissions } from "@/auth/permissions";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

export interface ListState {
  filter: Filter;
  columns: string[];
  sort: Sort[];
}

/**
 * Whether the list has drifted from the view it was opened with.
 *
 * Compared by serialising rather than field by field, with the keys of every
 * object put in one order first (`canonicalJson`): a `jsonb` column returns
 * them in an order of its own, so the same filter read from the API and decoded
 * from the URL serialise differently as written. The serialised form is still
 * the definition of "the same view" the rest of the system uses — and a
 * hand-written deep compare would be a second answer to that question, free to
 * disagree with the first.
 */
function sameState(a: ListState, b: ListState): boolean {
  return canonicalJson(a) === canonicalJson(b);
}

/**
 * Save what the list is showing — over the open view, or as a new one.
 *
 * One button now rather than the two the toolbar used to grow beside each
 * other: the reference bar has a single `Save` beside Columns and Sort, so
 * "which one is right for me" moved from the toolbar (two buttons, pick one)
 * into this popover (one button, offered a choice once it opens). Disabled
 * rather than absent when there is nothing to save — a bar whose last three
 * controls come and go by turns reads as flickering, where Columns and Sort
 * are always there.
 *
 * "Save to «view»" needs `settings.views.manage` (#349); "Save as new view"
 * does not, matching `SavedViewsAdmin`'s "creating a view does not, since
 * anyone who can already see a module can save one for it". Overwriting an
 * existing view is closer to the renaming and re-defaulting that screen does
 * gate — it changes what a *shared* view means for everyone who opens it,
 * not just what this browser is looking at.
 */
export function SaveViewButton({
  savedViews,
  current,
}: {
  /**
   * The same `useSavedViews(scope)` call `SavedViewPicker` holds, passed down
   * by `FilterBar` (#355) rather than called again here — `activeViewId` is
   * per-hook-instance state, so a second call would never see a view chosen
   * in the picker's.
   */
  savedViews: ReturnType<typeof useSavedViews>;
  current: ListState;
}) {
  const { views, activeViewId, redefine, save } = savedViews;
  const permissions = usePermissions();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const activeView = views.find((view) => view.id === activeViewId) ?? null;
  const canOverwrite = can(permissions, "settings.views.manage");

  const definitionOf = (view: SavedView): ListState => ({
    filter: view.filter,
    columns: view.columns,
    sort: view.sort,
  });

  /*
    The question is not "has anything changed since some tracked selection"
    but "is what I am looking at already saved somewhere". Asked that way it
    needs no selection tracking and is right in every case: a section opened
    on its default matches that view and offers nothing; a link someone was
    sent matches nothing and is worth keeping; a view someone then narrowed
    matches nothing either, and `activeView` still says which one to offer to
    overwrite.

    An earlier version compared against the active view alone and needed a
    `touched` flag to suppress it, because the default was marked active
    without ever being applied. #282 applies it, and the flag went with it.
  */
  const alreadySaved = views.some((view) =>
    sameState(definitionOf(view), current),
  );

  const worthSaving =
    !alreadySaved &&
    (activeView !== null ||
      current.filter.children.length > 0 ||
      current.sort.length > 0);

  /** A refusal is shown, never swallowed — same contract as the picker. */
  function handle(result: { ok: boolean; rejection?: { message: string } }) {
    if (result.ok) {
      setOpen(false);
      setName("");
      setProblem(null);
      return;
    }
    setProblem(result.rejection?.message ?? "Refused.");
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // An unchanged view has nothing this popover could do, so it does
        // not open — the spec's "does nothing" rather than an empty sheet.
        setOpen(next && worthSaving);
        if (!next) setProblem(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={!worthSaving}
          title={worthSaving ? undefined : "Nothing to save"}
        >
          Save
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-72">
        <div className="flex flex-col gap-2">
          {activeView && canOverwrite ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void redefine(activeView.id, current).then(handle);
                }}
              >
                Save to “{activeView.name}”
              </Button>
              <p className="text-muted-foreground text-center text-xs">or</p>
            </>
          ) : null}

          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim() === "") return;
              void save({ name: name.trim(), ...current }).then(handle);
            }}
          >
            <label htmlFor="view-name" className="text-sm font-medium">
              Save as new view
            </label>
            <Input
              id="view-name"
              autoFocus
              value={name}
              placeholder="COD awaiting dispatch"
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <p className="text-muted-foreground text-xs">
              Saves the filter, the columns and the sort as they are now.
            </p>
            {problem ? (
              <p className="text-destructive text-xs">{problem}</p>
            ) : null}
            <Button type="submit" size="sm" disabled={name.trim() === ""}>
              Save view
            </Button>
          </form>
        </div>
      </PopoverContent>
    </Popover>
  );
}
